import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Upload, CheckCircle2, Video, Trash2 } from "lucide-react";

type LessonRow = {
  slug: string;
  title: string;
  order_index: number;
  video_path: string | null;
  video_url: string | null;
};

const LessonVideosSection = () => {
  const [lessons, setLessons] = useState<LessonRow[]>([]);
  const [uploadingSlug, setUploadingSlug] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadLessons = async () => {
    const { data, error } = await supabase
      .from("lessons")
      .select("slug, title, order_index, video_path, video_url")
      .order("order_index", { ascending: true });
    if (error) {
      toast.error("Failed to load lessons");
      return;
    }
    setLessons((data || []) as LessonRow[]);
  };

  useEffect(() => {
    loadLessons().finally(() => setLoading(false));
  }, []);

  const handleUpload = async (lesson: LessonRow, file: File) => {
    if (!file.type.startsWith("video/")) {
      toast.error("Please choose a video file (mp4, mov, webm)");
      return;
    }
    if (file.size > 2 * 1024 * 1024 * 1024) {
      toast.error("File is larger than 2 GB");
      return;
    }
    setUploadingSlug(lesson.slug);
    const ext = file.name.split(".").pop() || "mp4";
    const path = `${lesson.slug}/${Date.now()}.${ext}`;

    if (lesson.video_path) {
      await supabase.storage.from("lesson-videos").remove([lesson.video_path]);
    }
    const { error: uploadError } = await supabase.storage
      .from("lesson-videos")
      .upload(path, file, { contentType: file.type, upsert: false });
    if (uploadError) {
      toast.error(`Upload failed: ${uploadError.message}`);
      setUploadingSlug(null);
      return;
    }
    const { error: updateError } = await supabase
      .from("lessons")
      .update({ video_path: path })
      .eq("slug", lesson.slug);
    if (updateError) {
      toast.error(`Saved video but failed to link: ${updateError.message}`);
      setUploadingSlug(null);
      return;
    }
    toast.success(`${lesson.title} uploaded`);
    setUploadingSlug(null);
    await loadLessons();
  };

  const handleRemove = async (lesson: LessonRow) => {
    if (!lesson.video_path) return;
    if (!confirm(`Remove uploaded video for "${lesson.title}"?`)) return;
    await supabase.storage.from("lesson-videos").remove([lesson.video_path]);
    const { error } = await supabase
      .from("lessons")
      .update({ video_path: null })
      .eq("slug", lesson.slug);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Video removed");
    await loadLessons();
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-3">
        <Video className="text-gold" size={20} />
        <h2 className="font-display text-2xl font-semibold text-foreground">Lesson Videos</h2>
      </div>
      <p className="text-muted-foreground font-body text-sm max-w-2xl">
        Upload each lesson video directly to your portal. Files stream from your own hosting —
        never sent to YouTube. Students see them without any external branding.
      </p>

      {loading ? (
        <p className="text-muted-foreground font-body text-sm">Loading lessons…</p>
      ) : lessons.length === 0 ? (
        <p className="text-muted-foreground font-body text-sm">No lessons found.</p>
      ) : (
        <div className="space-y-3">
          {lessons.map((lesson) => {
            const hasNativeVideo = !!lesson.video_path;
            const isUploading = uploadingSlug === lesson.slug;
            return (
              <div key={lesson.slug} className="border border-gold/20 bg-card/60 p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-gold font-body">
                      {lesson.order_index === 0 ? "Module" : `Lesson ${lesson.order_index}`}
                    </p>
                    <h3 className="font-display text-lg font-semibold text-foreground mt-1">
                      {lesson.title}
                    </h3>
                    <div className="mt-1 text-xs font-body flex items-center gap-2">
                      {hasNativeVideo ? (
                        <>
                          <CheckCircle2 size={14} className="text-gold" />
                          <span className="text-gold">Hosted here</span>
                        </>
                      ) : (
                        <span className="text-muted-foreground">No video yet</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label
                      className={`inline-flex items-center gap-2 px-4 py-2 border font-body text-xs font-semibold uppercase tracking-[0.2em] cursor-pointer ${
                        isUploading
                          ? "border-gold/20 text-muted-foreground cursor-wait"
                          : "border-gold/40 text-gold hover:bg-gold/10"
                      }`}
                    >
                      <Upload size={14} />
                      {isUploading ? "Uploading…" : hasNativeVideo ? "Replace" : "Upload"}
                      <input
                        type="file"
                        accept="video/*"
                        className="hidden"
                        disabled={isUploading}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleUpload(lesson, f);
                          e.target.value = "";
                        }}
                      />
                    </label>
                    {hasNativeVideo && !isUploading && (
                      <button
                        onClick={() => handleRemove(lesson)}
                        className="p-2 border border-destructive/40 text-destructive hover:bg-destructive/10"
                        title="Remove uploaded video"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default LessonVideosSection;
